import { inject } from '@angular/core';
import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpErrorResponse,
} from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import rg4js from 'raygun4js';

import { Ams } from 'src/app/amsconfig';

// Cache for auth token from URL - persists across requests during PDF generation
let cachedAuthToken: string | null = null;

// Returns the cached PDF-generation auth token (extracting and caching from URL on
// first call). Exported so SignalR setup can also retrieve it, since WebSockets can't
// send the X-Auth-Token header and must pass the token via ?access_token= query param.
export const getPdfAuthToken = (): string | null => getAuthTokenFromUrl();

// Extract auth token from URL query params (for PDF generation)
// Immediately removes token from URL to prevent leakage via Referer/history
// Caches the token so it can be used for all subsequent requests
const getAuthTokenFromUrl = (): string | null => {
  // Return cached token if we have one
  if (cachedAuthToken) {
    return cachedAuthToken;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const authToken = urlParams.get('auth');

  // If no auth token, return early
  if (!authToken) {
    return null;
  }

  // Validate token format (base64url characters, reasonable length)
  // Adjust pattern based on your actual token format
  // This pattern allows base64url characters (A-Za-z0-9-_) and dots for JWT
  // with a length between 20 and 2000 characters
  const tokenPattern = /^[A-Za-z0-9\-_\.]{20,2000}$/;

  if (!tokenPattern.test(authToken)) {
    console.warn('Invalid auth token format detected');
    // Still remove invalid token from URL
    removeAuthFromUrl();
    return null;
  }

  // Cache the token before removing from URL
  cachedAuthToken = authToken;

  // Remove auth token from URL immediately to prevent leakage
  removeAuthFromUrl();

  return authToken;
};

// Helper function to remove auth parameter from URL without reload
const removeAuthFromUrl = (): void => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('auth');
    
    // Use replaceState to update URL without adding to history
    // This prevents the token from being in browser history or sent as Referer
    window.history.replaceState(null, '', url.toString());
  } catch (error) {
    console.error('Failed to remove auth token from URL:', error);
  }
};

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn) => {
  const snackBar = inject(MatSnackBar);
  
  // Check if we have an auth token from query params
  const authToken = getAuthTokenFromUrl();

  let authReq = req.clone({
    withCredentials: true,
  });

  // If we have an auth token from query params, add it as a header
  if (authToken) {
    authReq = authReq.clone({
      setHeaders: {
        'X-Auth-Token': authToken
      }
    });
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      rg4js('send', {
        error: 'Failed HTTP request',
        customData: { rejection: error },
      });

      if (error.status === 401) {
        // Don't redirect to login if we're using URL-based auth token (PDF generation mode)
        // The token might be expired/invalid, but we shouldn't redirect the headless browser
        if (!cachedAuthToken) {
          snackBar.open('Authorization Failed!', 'Close', {
            duration: 3000,
            verticalPosition: 'top',
            horizontalPosition: 'right',
          });

          window.location.href = `${Ams.Config.BASE_URL}/auth/login?redirectUrl=${encodeURIComponent(
            window.location.href
          )}`;
        } else {
          console.error('PDF generation auth token is invalid or expired');
        }
      }

      return throwError(() => error);
    })
  );
};
